// Run status groups the pages filter and count by.

import { RunStatus, type Run } from '@goblic/shared'
import { isScheduledResume, isWaitingOnPerson } from './run-recovery'

export const ACTIVE_RUN_STATUSES: RunStatus[] = [
  RunStatus.Queued,
  RunStatus.Provisioning,
  RunStatus.Running,
  RunStatus.Verifying,
]

export const FAILED_RUN_STATUSES: RunStatus[] = [
  RunStatus.Failed,
  RunStatus.TimedOut,
  RunStatus.Blocked,
]

/**
 * Statuses that *can* wait on a person — an `awaiting_approval` run still
 * holding its sandbox, or one paused past the 15-minute live-wait window.
 * A `paused` run with `resumeAfter` (usage limit, "Resume at …") resumes by
 * itself and is not waiting on anyone: filter with `isWaitingOnPerson`
 * (lib/run-recovery.ts) where the run itself is at hand.
 */
export const WAITING_RUN_STATUSES: RunStatus[] = [
  RunStatus.AwaitingApproval,
  RunStatus.Paused,
]

export const FINISHED_RUN_STATUSES: RunStatus[] = [
  RunStatus.Completed,
  RunStatus.Cancelled,
  RunStatus.HandedOff,
]

export type RunView = 'active' | 'waiting' | 'failed' | 'completed' | 'all'

/**
 * Which tab of the runs list a run belongs to. A quota-paused run counts as
 * working (it resumes by itself), not as "waiting for you"; a handed-off run
 * is finished (its work goes on in the child run).
 */
export function runInView(run: Pick<Run, 'status' | 'resumeAfter'>, view: RunView): boolean {
  switch (view) {
    case 'all':
      return true
    case 'active':
      return ACTIVE_RUN_STATUSES.includes(run.status) || isScheduledResume(run)
    case 'waiting':
      return isWaitingOnPerson(run)
    case 'failed':
      return FAILED_RUN_STATUSES.includes(run.status)
    case 'completed':
      return FINISHED_RUN_STATUSES.includes(run.status)
  }
}
